import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pause, Play, ChevronLeft, ChevronRight, Maximize } from 'lucide-react';
import { useFollowFeedStories, useMarkStoryAsViewed, UserStories } from '@/hooks/use-follow-feed-stories';
import BulletinCard from '../bulletins/BulletinCard';
import BulletinDetailView from '../bulletins/BulletinDetailView';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

interface FollowFeedStoryViewerProps {
  initialUserId?: string;
  onClose: () => void;
}

const STORY_DURATION = 5000; // 5 seconds per story

const FollowFeedStoryViewer = ({ initialUserId, onClose }: FollowFeedStoryViewerProps) => {
  const { data: userStories = [], isLoading } = useFollowFeedStories();
  const markAsViewed = useMarkStoryAsViewed();
  
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Find initial user index if specified
  useEffect(() => {
    if (initialUserId && userStories.length > 0) {
      const index = userStories.findIndex(user => user.user_id === initialUserId);
      if (index !== -1) {
        setCurrentUserIndex(index);
      }
    }
  }, [initialUserId, userStories]);

  const currentUser = userStories[currentUserIndex];
  const currentStory = currentUser?.bulletins[currentStoryIndex];

  // Progress timer (pause when fullscreen is open)
  useEffect(() => {
    if (!currentStory || isPaused || isFullscreen) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (STORY_DURATION / 50));
        if (newProgress >= 100) {
          nextStory();
          return 0;
        }
        return newProgress;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [currentStory?.id, isPaused]);

  // Mark story as viewed when it starts
  useEffect(() => {
    if (currentStory?.id) {
      markAsViewed.mutate(currentStory.id);
    }
  }, [currentStory?.id, markAsViewed]);

  const nextStory = useCallback(() => {
    if (!currentUser) return;

    if (currentStoryIndex < currentUser.bulletins.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
      setProgress(0);
    } else {
      // Move to next user
      if (currentUserIndex < userStories.length - 1) {
        setCurrentUserIndex(prev => prev + 1);
        setCurrentStoryIndex(0);
        setProgress(0);
      } else {
        onClose();
      }
    }
  }, [currentUser, currentStoryIndex, currentUserIndex, userStories.length, onClose]);

  const prevStory = useCallback(() => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
      setProgress(0);
    } else if (currentUserIndex > 0) {
      const prevUser = userStories[currentUserIndex - 1];
      setCurrentUserIndex(prev => prev - 1);
      setCurrentStoryIndex(prevUser.bulletins.length - 1);
      setProgress(0);
    }
  }, [currentStoryIndex, currentUserIndex, userStories]);

  const goToUser = (userIndex: number) => {
    setCurrentUserIndex(userIndex);
    setCurrentStoryIndex(0);
    setProgress(0);
  };

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsPaused(false);
    const touch = e.changedTouches[0];
    const screenWidth = window.innerWidth;
    
    if (touch.clientX < screenWidth / 3) {
      prevStory();
    } else if (touch.clientX > (screenWidth * 2) / 3) {
      nextStory();
    }
  };

  if (isLoading || !currentUser || !currentStory) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      {/* Fullscreen Detail View */}
      {isFullscreen && currentStory && (
        <BulletinDetailView
          bulletin={currentStory}
          selectedId={currentStory.id}
          onLike={() => {}}
          onDelete={() => {}}
          onClose={handleCloseFullscreen}
        />
      )}

      <div className="fixed inset-0 bg-black z-50">
        {/* Header with progress bars */}
        <div className="absolute top-0 left-0 right-0 z-60 p-4">
          {/* Progress bars for current user's stories */}
          <div className="flex gap-1 mb-4">
            {currentUser.bulletins.map((_, index) => (
              <div
                key={index}
                className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
              >
                <div
                  className="h-full bg-white transition-all duration-100"
                  style={{
                    width: index < currentStoryIndex ? '100%' : 
                           index === currentStoryIndex ? `${progress}%` : '0%'
                  }}
                />
              </div>
            ))}
          </div>

          {/* User info and controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="w-8 h-8 border-2 border-white">
                <AvatarImage src={currentUser.profile.profile_image_url || undefined} />
                <AvatarFallback className="text-xs">
                  {currentUser.profile.username[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white font-semibold text-sm">
                  {currentUser.profile.display_name || `@${currentUser.profile.username}`}
                </p>
                <p className="text-white/70 text-xs">
                  {new Date(currentStory.created_at).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFullscreen}
                className="text-white hover:bg-white/10"
              >
                <Maximize className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsPaused(!isPaused)}
                className="text-white hover:bg-white/10"
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

        </div>

        {/* Story content */}
        <div 
          className="absolute inset-x-0 top-20 bottom-0 flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Navigation areas */}
          <button
            onClick={prevStory}
            className="absolute left-0 top-0 w-1/3 h-full z-40 opacity-0"
            disabled={currentUserIndex === 0 && currentStoryIndex === 0}
          />
          <button
            onClick={nextStory}
            className="absolute right-0 top-0 w-1/3 h-full z-40 opacity-0"
          />


          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentUser.user_id}-${currentStoryIndex}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full max-w-md mx-auto pb-8"
            >
              <BulletinCard
                bulletin={currentStory}
                size="large"
                isSelected={true}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop navigation buttons */}
        <div className="hidden md:block">
          <Button
            variant="ghost"
            size="icon"
            onClick={prevStory}
            disabled={currentUserIndex === 0 && currentStoryIndex === 0}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-40"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={nextStory}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-40"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default FollowFeedStoryViewer;